import { useState } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Apple,
  Bot,
  Copy,
  Check,
  Download,
  ExternalLink,
  Monitor,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import {
  RAW_HOSTS_URL,
  IOS_SURGE_URL,
  DOWNLOAD_WINDOWS_HOSTS,
  DOWNLOAD_ANDROID_HOSTS,
  DOWNLOAD_IOS_SURGE,
  APK_DOWNLOAD_URL,
} from "@/lib/config";

const HOSTS_CONTENT = `149.202.58.185 www.growtopia1.com
149.202.58.185 www.growtopia2.com`;

const SURGE_CONTENT = `[General]
bypass-system = true

[Rule]
FINAL,DIRECT

[Host]
growtopia1.com = 149.202.58.185
growtopia2.com = 149.202.58.185
www.growtopia1.com = 149.202.58.185
www.growtopia2.com = 149.202.58.185`;

function CodeBlock({ content, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="relative">
      <pre
        data-testid={`${testid}-content`}
        className="font-mono text-[12.5px] leading-relaxed text-slate-200 bg-black/40 border border-white/10 rounded-xl p-4 overflow-x-auto scrollbar-none"
      >
        {content}
      </pre>
      <button
        onClick={onCopy}
        data-testid={`${testid}-copy-btn`}
        className="absolute top-2.5 right-2.5 inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.05] px-2 py-1 text-[11px] text-slate-200 hover:bg-white/[0.1] transition-colors"
      >
        {copied ? (
          <>
            <Check className="h-3 w-3 text-emerald-400" /> Copied
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" /> Copy
          </>
        )}
      </button>
    </div>
  );
}

function UrlPill({ label, url, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(`${label} URL copied`);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2 pl-3 min-w-0">
      <span className="font-mono text-[12px] text-slate-300 truncate flex-1 min-w-0">
        {url}
      </span>
      <button
        onClick={onCopy}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.05] px-2.5 py-1.5 text-[11px] text-white hover:bg-white/[0.1] transition-colors shrink-0 whitespace-nowrap"
      >
        {copied ? (
          <Check className="h-3 w-3 text-emerald-400" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
        Copy URL
      </button>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="mt-[2px] grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gradient-to-b from-amber-200 to-amber-500 text-[11px] font-bold text-amber-950 ring-1 ring-white/30 shadow-[0_4px_12px_-4px_rgba(250,204,21,0.5)]">
        {n}
      </span>
      <p className="text-sm leading-relaxed text-slate-300">{children}</p>
    </li>
  );
}

function PlatformPanel({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div data-testid={testid} className="grid lg:grid-cols-2 gap-6 lg:gap-8 p-1 min-w-0">
      <div className="min-w-0">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-slate-300">
          <Icon className="h-3.5 w-3.5 text-amber-300" />
          {title}
        </div>
        <h3 className="font-display mt-3 text-2xl sm:text-3xl font-medium tracking-tight text-white">
          {subtitle}
        </h3>
        <div className="mt-5">{children.steps}</div>
      </div>
      <div className="space-y-4 min-w-0">{children.aside}</div>
    </div>
  );
}

export default function HowToPlay() {
  return (
    <section
      id="how-to-play"
      data-testid="how-to-play-section"
      className="relative mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32"
    >
      <div className="mx-auto max-w-2xl text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-slate-300">
          <Download className="h-3 w-3 text-amber-300" />
          Setup guide
        </div>
        <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
          How to play on OriginPS
        </h2>
        <p className="mt-3 text-base sm:text-lg text-slate-400">
          Pick your platform below. Follow the steps and you&apos;ll be in-game in
          minutes.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="mt-12 glass-card ring-border p-3 sm:p-6 lg:p-8"
      >
        <Tabs defaultValue="windows" className="w-full">
          <TabsList
            data-testid="how-to-play-tabs"
            className="grid w-full grid-cols-3 gap-2 bg-white/[0.03] border border-white/10 p-1 rounded-2xl h-auto"
          >
            <TabsTrigger
              value="windows"
              data-testid="tab-windows"
              className="rounded-xl text-slate-300 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white data-[state=active]:shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] py-2.5 transition-all"
            >
              <Monitor className="h-4 w-4 mr-2" />
              Windows
            </TabsTrigger>
            <TabsTrigger
              value="android"
              data-testid="tab-android"
              className="rounded-xl text-slate-300 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white data-[state=active]:shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] py-2.5 transition-all"
            >
              <Bot className="h-4 w-4 mr-2" />
              Android
            </TabsTrigger>
            <TabsTrigger
              value="ios"
              data-testid="tab-ios"
              className="rounded-xl text-slate-300 data-[state=active]:bg-white/[0.1] data-[state=active]:text-white data-[state=active]:shadow-[inset_0_1px_0_rgba(255,255,255,0.1)] py-2.5 transition-all"
            >
              <Apple className="h-4 w-4 mr-2" />
              iOS
            </TabsTrigger>
          </TabsList>

          {/* WINDOWS */}
          <TabsContent value="windows" className="mt-6 sm:mt-8">
            <PlatformPanel
              icon={Monitor}
              title="Windows"
              subtitle="Edit your hosts file"
              testid="panel-windows"
            >
              {{
                steps: (
                  <ol className="space-y-4">
                    <Step n={1}>
                      Download the <span className="text-white">hosts.txt</span>{" "}
                      file using the button on the right.
                    </Step>
                    <Step n={2}>
                      Copy the contents into{" "}
                      <code className="font-mono text-amber-300">
                        C:\Windows\System32\drivers\etc\hosts
                      </code>{" "}
                      (open Notepad as Administrator).
                    </Step>
                    <Step n={3}>
                      This file maps <code className="font-mono text-amber-300">growtopia.com</code> to our server.
                    </Step>
                    <Step n={4}>
                      Open Growtopia — it will connect to OriginPS
                      automatically. Enjoy the game!
                    </Step>
                  </ol>
                ),
                aside: (
                  <>
                    <CodeBlock content={HOSTS_CONTENT} testid="windows-hosts" />
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                      <a
                        href={DOWNLOAD_WINDOWS_HOSTS}
                        data-testid="download-windows-hosts-btn"
                        className="glossy-pill inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap w-full sm:w-auto transition-transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        <Download className="h-4 w-4" />
                        Download hosts.txt
                      </a>
                    </div>
                  </>
                ),
              }}
            </PlatformPanel>
          </TabsContent>

          {/* ANDROID */}
          <TabsContent value="android" className="mt-6 sm:mt-8">
            <PlatformPanel
              icon={Bot}
              title="Android"
              subtitle="Use a hosts manager"
              testid="panel-android"
            >
              {{
                steps: (
                  <ol className="space-y-4">
                    <Step n={1}>
                      Download the APK file below, or download the <span className="text-white">OriginPS.txt</span> file for hosts-based connection.
                    </Step>
                    <Step n={2}>
                      Install a hosts manager such as{" "}
                      <span className="text-white">Virtual Hosts</span> or{" "}
                      <span className="text-white">PowerTunnel</span> from the Play Store.
                    </Step>
                    <Step n={3}>
                      Import the downloaded file (or paste the URL as a config source) into the app.
                    </Step>
                    <Step n={4}>
                      Enable the hosts manager, open Growtopia and you&apos;ll be connected to OriginPS.
                    </Step>
                  </ol>
                ),
                aside: (
                  <>
                    <CodeBlock content={HOSTS_CONTENT} testid="android-hosts" />
                    <UrlPill
                      label="Powertunnel config"
                      url={RAW_HOSTS_URL}
                      testid="copy-android-config-url"
                    />
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:justify-start items-stretch sm:items-center">
                      <a
                        href={APK_DOWNLOAD_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="download-android-apk-btn"
                        className="glossy-pill inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap w-full sm:w-auto transition-transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        <Download className="h-4 w-4" />
                        Download APK
                      </a>
                      <a
                        href={DOWNLOAD_ANDROID_HOSTS}
                        data-testid="download-android-hosts-btn"
                        className="glossy-pill inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap w-full sm:w-auto transition-transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        <Download className="h-4 w-4" />
                        Download OriginPS.txt
                      </a>
                      <a
                        href={RAW_HOSTS_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="open-android-raw-btn"
                        className="ghost-pill inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium whitespace-nowrap w-full sm:w-auto transition-all"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open raw URL
                      </a>
                    </div>
                  </>
                ),
              }}
            </PlatformPanel>
          </TabsContent>

          {/* iOS */}
          <TabsContent value="ios" className="mt-6 sm:mt-8">
            <PlatformPanel
              icon={Smartphone}
              title="iOS"
              subtitle="Import Surge config"
              testid="panel-ios"
            >
              {{
                steps: (
                  <ol className="space-y-4">
                    <Step n={1}>
                      Install <span className="text-white">Surge</span> on your iPhone or iPad from the App Store.
                    </Step>
                    <Step n={2}>
                      Copy the Surge config (or use the config URL on the right).
                    </Step>
                    <Step n={3}>
                      Open Surge → import config from clipboard or URL.
                    </Step>
                    <Step n={4}>
                      Enable Surge. It will redirect Growtopia to our server. Open Growtopia and enjoy!
                    </Step>
                  </ol>
                ),
                aside: (
                  <>
                    <CodeBlock content={SURGE_CONTENT} testid="ios-surge" />
                    <UrlPill
                      label="Surge config"
                      url={IOS_SURGE_URL}
                      testid="copy-ios-config-url"
                    />
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:justify-start items-stretch sm:items-center">
                      <a
                        href={DOWNLOAD_IOS_SURGE}
                        data-testid="download-ios-surge-btn"
                        className="glossy-pill inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap w-full sm:w-auto transition-transform hover:scale-[1.02] active:scale-[0.98]"
                      >
                        <Download className="h-4 w-4" />
                        Download Surge config
                      </a>
                      <a
                        href={IOS_SURGE_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="open-ios-raw-btn"
                        className="ghost-pill inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium whitespace-nowrap w-full sm:w-auto transition-all"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open raw URL
                      </a>
                    </div>
                  </>
                ),
              }}
            </PlatformPanel>
          </TabsContent>
        </Tabs>
      </motion.div>
    </section>
  );
}
